import React, { ReactNode, Fragment } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { CommunityLink } from '@lumapps/communities/components/CommunityLink';
import { ContentTypes } from '@lumapps/content-types/types';
import { ColorPalette, Link, Text, Theme } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';
import { TagReference } from '@lumapps/widget-base/types';

import { getPostPublicationTranslationKey } from '../../../keys';
import { BlockPostIdeaStatus, PostShare } from '../../../types';
import { PostIdeaStatus } from '../PostIdeaStatus';

export interface PostPublicationDescriptionProps {
    /** Post share information */
    postShare?: PostShare;
    /** Categories of the tag. */
    tags?: TagReference[];
    /** Idea post status */
    ideaStatus?: BlockPostIdeaStatus;
    /** Id of the current community */
    currentCommunityId?: string;
    /** HTML id to set on the description */
    id?: string;
    /** Theme */
    theme?: Theme;
}

const CLASSNAME = 'post-publication-description';

/**
 * Component to render author's information in a post.
 * Display current community if has communityName props
 * Display categories if an array of tags is given
 */
export const PostPublicationDescription: React.FC<PostPublicationDescriptionProps> = ({
    currentCommunityId,
    postShare,
    tags,
    ideaStatus,
    id,
    theme = Theme.light,
}) => {
    const { translateAndReplaceWithComponents } = useTranslate();
    const { element } = useClassnames(CLASSNAME);
    const isPostCommunityPage = Boolean(currentCommunityId) && currentCommunityId === postShare?.sharedTo?.communityId;

    // Block displayed as location
    let locationBlock: ReactNode;
    const textColor = theme === Theme.dark ? ColorPalette.light : ColorPalette.primary;

    // In community page => show the post tags.
    if (isPostCommunityPage && tags && tags.length > 0) {
        locationBlock = tags.map((tag, idx) => (
            <Fragment key={tag.tagId}>
                {idx !== 0 && ' - '}
                <Text as="span" className={element('tags')} color={textColor}>
                    {tag.name}
                </Text>
            </Fragment>
        ));
    }
    // Outside the community => show the community link.
    if (!isPostCommunityPage && postShare?.sharedTo) {
        locationBlock = (
            <Link
                className={element('community')}
                color={textColor}
                linkAs={CommunityLink}
                to={{
                    id: postShare.sharedTo.communityId,
                    slug: postShare.sharedTo.slug,
                    renderingType: postShare.sharedTo.renderingType,
                    type: ContentTypes.COMMUNITY,
                    instance: { id: postShare.site?.siteId, slug: postShare.site?.slug },
                }}
            >
                {postShare.sharedTo.name}
            </Link>
        );
    }

    return (
        <Text as="span" id={id}>
            {translateAndReplaceWithComponents(
                getPostPublicationTranslationKey({
                    hasLocation: Boolean(locationBlock),
                    hasStatus: Boolean(ideaStatus),
                }),
                {
                    LOCATION: <React.Fragment key="location">{locationBlock}</React.Fragment>,
                    STATUS: (
                        <PostIdeaStatus
                            className={element('tags')}
                            ideaStatus={ideaStatus}
                            key="post-idea-status"
                            theme={theme}
                        />
                    ),
                },
            )}
        </Text>
    );
};
